// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Third party
import { takeUntil } from 'rxjs';
import * as _ from 'lodash';

// Local library
import {
  AccountFacade,
  AccountService
} from '@amwager/account/data-access';
import {
  DateFormatPipe,
  MatNotificationService,
  OptInStatusPipe,
  StripHtmlPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { IGetPromoResponse } from '@amwager/util-ts';

/** Identifiers for the promotions tab strip. */
type PromoTabId = 'giveaway' | 'free-spin' | 'refer-a-friend';

/** Promo list item with a transient per-item claim spinner flag. */
type PromoItem = IGetPromoResponse & { isLoading?: boolean };

/**
 * Promotions panel — the tabbed "Promotions" card shown in the profile.
 *
 * Ports the promo logic from the Ionic `PromosComponent`: it loads the promo
 * list from the API, sorts/counts it via `AccountService`, and handles claiming
 * a promo/giveaway (one at a time). Keeps the existing web UI design.
 */
@Component({
  selector: 'feature-mat-promotions',
  templateUrl: './promotions.component.html',
  styleUrls: ['./promotions.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    DateFormatPipe,
    OptInStatusPipe,
    StripHtmlPipe,
    TranslatePipe
  ]
})
export class PromotionsComponent extends UnsubscribeObservableService implements OnInit, OnDestroy {

  promoList: PromoItem[] = [];
  promoCount = 0;
  isProcessing = false;

  // Placeholder rows rendered by the loading skeleton.
  readonly skeletonRows = [1, 2, 3, 4];

  // One claim in flight at a time (was `isPromoTimeout` in the Ionic version).
  private isPromoTimeout = false;
  private currentUsedPromo: PromoItem | null = null;

  // Placeholder counts until Free Spin / Refer a Friend are wired to an API.
  freeSpinCount = 0;
  referAFriendCount = 0;

  activePromoTab: PromoTabId = 'giveaway';
  // `hidden` tabs stay defined but aren't rendered yet (Free Spin / Refer a Friend).
  readonly promoTabs: { id: PromoTabId; label: string; hidden?: boolean }[] = [
    { id: 'giveaway', label: 'Giveaway' },
    { id: 'free-spin', label: 'Free Spin', hidden: true },
    { id: 'refer-a-friend', label: 'Refer a Friend', hidden: true }
  ];

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly accountService: AccountService,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  /**
   * Fetch promos and subscribe to the list / claim-result streams.
   * @returns {void}
   */
  ngOnInit(): void {
    this.isProcessing = true;
    this.accountFacade.getPromoListAPI();

    this.accountFacade.getPromoList$()
      .pipe(takeUntil(this.toUnsubscribe$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((promoList: any) => {
        if (!promoList) return;
        if (promoList?.isSuccess === undefined) return;

        this.promoList = this.accountService.sortByEndAndStart(promoList.response);
        this.promoCount = this.accountService.countAvailablePromoGiveaways(this.promoList);
        this.isProcessing = false;

        // A resolved list re-emit clears the in-flight claim spinner.
        this.clearPendingClaim();

        this.cdRef.markForCheck();
      });

    this.accountFacade.useGiveAway$()
      .pipe(takeUntil(this.toUnsubscribe$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((useGiveAway: any) => {
        if (useGiveAway?.isSuccess === false) {
          this.showAlertError(useGiveAway?.response?.error?.Message);
          this.accountFacade.clearGiveAway();
          this.clearPendingClaim();
        }
        this.cdRef.markForCheck();
      });

    this.accountFacade.usePromo$()
      .pipe(takeUntil(this.toUnsubscribe$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((usePromo: any) => {
        if (usePromo?.isSuccess === false) {
          this.showAlertError(usePromo?.response?.error?.Message);
          this.accountFacade.clearPromo();
          this.clearPendingClaim();
        }
        this.cdRef.markForCheck();
      });
  }

  /**
   * Tear down parent unsubscribe stream on destroy.
   * @returns {void}
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Claim a promo/giveaway. Ignored when another claim is in flight.
   * @param {PromoItem} promo Promo to claim
   * @returns {void}
   */
  usePromo(promo: PromoItem): void {
    if (!promo.name) return;
    if (this.isPromoTimeout) return;

    this.isPromoTimeout = true;
    this.currentUsedPromo = promo;
    this.promoList = _.map(this.promoList, (p: PromoItem) =>
      p.name === promo.name ? { ...p, isLoading: true } : p
    );

    if (promo.type === 'Giveaway') this.accountFacade.useGiveAwayAPI(promo.name);
    if (promo.type === 'Promo') this.accountFacade.usePromoAPI(promo.name);

    this.cdRef.markForCheck();
  }

  /**
   * Show an error alert via the shared Material dialog.
   * @param {string} error Error message to display
   * @returns {void}
   */
  showAlertError(error: string): void {
    this.matNotificationService.showAlert('Error', error, '', '', 'Close');
  }

  /**
   * Badge count for a promotions tab.
   * @param {PromoTabId} id Tab identifier
   * @returns {number} Count to display
   */
  promoTabCount(id: PromoTabId): number {
    if (id === 'free-spin') return this.freeSpinCount;
    if (id === 'refer-a-friend') return this.referAFriendCount;
    return this.promoCount;
  }

  /**
   * Switch the active promotions tab.
   * @param {PromoTabId} id Tab to activate
   * @returns {void}
   */
  selectPromoTab(id: PromoTabId): void {
    this.activePromoTab = id;
    this.cdRef.markForCheck();
  }

  /**
   * Track promos by name (used in @for).
   * @param {number} index Current index
   * @param {PromoItem} promo Current promo
   * @returns {string} Stable key
   */
  trackByName(index: number, promo: PromoItem): string {
    return promo?.name ?? `${index}`;
  }

  /**
   * Clear the in-flight claim spinner/guard for the pending promo.
   * @returns {void}
   */
  private clearPendingClaim(): void {
    if (!this.isPromoTimeout) return;

    this.promoList = _.map(this.promoList, (p: PromoItem) =>
      p.name === this.currentUsedPromo?.name ? { ...p, isLoading: false } : p
    );
    this.isPromoTimeout = false;
    this.currentUsedPromo = null;
  }
}
